import { FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAdditionalFormContext } from '../../../common/form';
import { encodeStringKey } from '../../../common/utils';
import {
  AllClassifierCategoriesQueryParams,
  useAllClassifierCategoriesQuery,
} from '../../../classifier';
import {
  AlgorithmJoinItemSelectField,
  AlgorithmJoinItemSelectFieldProps,
} from './AlgorithmJoinItemSelectField';
import { AlgorithmJoinsFormStore } from './AlgorithmJoinsFormStore';
import { AlgorithmJoinsValidFormDataType } from './schema/types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmJoinItemMappingValueSelectField.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-join-item-mapping-value-select-field',
);
addTranslations(translations);

export interface AlgorithmJoinItemMappingValueSelectFieldProps
  extends Omit<
    AlgorithmJoinItemSelectFieldProps,
    'name' | 'label' | 'value' | 'options' | 'onSelect' | 'onClear'
  > {
  joinIndex: number;
  index: number;
  role: 'source' | 'target';
  mappingAlias?: string;
}

export const AlgorithmJoinItemMappingValueSelectField: FC<
  AlgorithmJoinItemMappingValueSelectFieldProps
> = (props) => {
  const { joinIndex, index, role, mappingAlias, ...rest } = props;
  const { t } = useTranslation();

  const [mappings] = AlgorithmJoinsFormStore.useValue('mappings');
  const mapping = useMemo(
    () => mappings.find((m) => m.alias === mappingAlias),
    [mappings, mappingAlias],
  );

  const { data, isFetching } = useAllClassifierCategoriesQuery(
    {
      classifierCode: mapping?.classifierToCode,
    } as AllClassifierCategoriesQueryParams,
    { enabled: Boolean(mapping?.classifierToCode) },
  );

  const options = useMemo(
    () =>
      (data || []).map((category) => {
        const { code, name } = category;
        const key = encodeStringKey('category', code);
        return {
          key,
          value: key,
          label: `${code} - ${name}`,
          displayLabel: `${code} - ${name}`,
          category,
        };
      }),
    [data],
  );

  const { setValue } = useFormContext<AlgorithmJoinsValidFormDataType>();
  const { onChange: formOnChange } = useAdditionalFormContext();

  const name = `joins.${joinIndex}.algorithmJoinItems.${index}` as const;
  const value = useWatch<AlgorithmJoinsValidFormDataType, typeof name>({
    name,
  });

  const fieldName = role === 'source' ? 'sourceValue' : 'targetValue';

  const selectedValue = useMemo(() => {
    if (role === 'source' && 'sourceValue' in value) {
      return value.sourceValue
        ? encodeStringKey('category', value.sourceValue)
        : '';
    }

    if (role === 'target' && 'targetValue' in value) {
      return value.targetValue
        ? encodeStringKey('category', value.targetValue)
        : '';
    }

    return '';
  }, [value, role]);

  const handleSelect: AlgorithmJoinItemSelectFieldProps['onSelect'] =
    useCallback(
      (_value, option) => {
        setValue(`${name}.${fieldName}`, option.category.code, {
          shouldDirty: true,
        });
        formOnChange?.();
      },
      [name, fieldName, setValue, formOnChange],
    );

  const handleClear: AlgorithmJoinItemSelectFieldProps['onClear'] =
    useCallback(() => {
      setValue(`${name}.${fieldName}`, '', { shouldDirty: true });
      formOnChange?.();
    }, [name, fieldName, setValue, formOnChange]);

  return (
    <AlgorithmJoinItemSelectField
      name={`${name}.${fieldName}`}
      label={t(`${role}-label`)}
      value={selectedValue}
      onSelect={handleSelect}
      onClear={handleClear}
      options={options}
      loading={isFetching}
      disabled={isFetching}
      {...rest}
    />
  );
};
